import { KalturaOTTUser } from '@kaltura-ott/ott-client-types/api/types';
import {
  getUser,
  KuxConfig,
  kuxUpdateConfig,
  tvpilGetLocalCache,
  DynamicDataTypeEnum,
  FFUser,
  setEpgLimitCache,
  MediaType,
  getCacheValue,
  setCacheValue,
} from '@kaltura-ott/tvpil';
import { createUserStoreEvent, tvpilJournal } from '@kaltura-ott/tvpil-utils';
import { action, computed, observable } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { container, singleton } from 'tsyringe';

import type { LoginStore } from '../../stores/user/login-store';
import { errorHandling } from '../../utils/error-handling';
import { featureEnabler } from '../../utils/feature-enabler';
import { toast } from '../../utils/toast';
import { AppParametersStore } from '../app/app-params-store';
import type { AppStore } from '../app/app-store';
import type { LastWatchedLinearStore } from '../channelStats/last-watched-linear-store';
import { DmsStore } from '../dms';
import { ErrorPriority } from '../error';
import type { LinearRepository } from '../linearRepository/linear-repository';
import { ProvidersRepository } from '../providersRepository';
import { RecordRepository } from '../recordRepository';

import { DeviceStore } from './device-store';
import { getMulticastQualityValue } from './user-store.helpers';

const USER_BITRATE = 'userBitrate';
const DATA_SAVING_MODE = 'dataSavingMode';
const IDLE_USER_SELECTION_KEY = 'idleUserSelection';

const KS_TTL = 365 * 10 * 24 * 60 * 60 * 1000;

/**
 * @category Store
 */
@singleton()
export class UserStore {
  static moduleName = 'UserStore';

  @observable userData?: KalturaOTTUser;
  @observable userBitrate?: number;
  @observable dataSavingMode = false;
  @observable customerType = '';
  @observable authError = false;
  @observable isReady = false;
  @observable multiCastQuality?: string;
  @observable public idleSelection = false;

  private dmsStore?: DmsStore;
  private deviceStore?: DeviceStore;
  private appParametersStore: AppParametersStore;
  private recordRepository?: RecordRepository;

  @computed get username(): string | undefined {
    return this.userData?.username;
  }

  constructor() {
    this.appParametersStore = container.resolve<AppParametersStore>('appParamsStore');
  }

  init() {
    this.dmsStore = container.resolve<DmsStore>('dmsStore');
    this.deviceStore = container.resolve<DeviceStore>('deviceStore');
    this.recordRepository = container.resolve<RecordRepository>('recordRepository');
  }

  private async initLinearRepository() {
    const linearRepository = container.resolve<LinearRepository>('linearRepository');
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    await linearRepository.init(this.dmsStore!.dms.mediaTypes[MediaType.Linear]);

    tvpilJournal.log(createUserStoreEvent('LinearsReady'));
  }

  async setupUser() {
    tvpilJournal.log(createUserStoreEvent('SetupUserStart'));

    const loginStore = container.resolve<LoginStore>('loginStore');
    const appStore = container.resolve<AppStore>('appStore');

    if (!loginStore) {
      return;
    }
    const { ks } = await loginStore.getCredentials();

    if (ks) {
      tvpilJournal.log(createUserStoreEvent('SetupUserWithKs'));

      loginStore.isPostLoginProcessing = true;
      loginStore.toggleAnonymous(false);
      loginStore.setKs(ks);
      kuxUpdateConfig({ ks: ks } as KuxConfig);
      await Promise.all([
        (async () => {
          await this.initLinearRepository();
          const lastWatchedLinear = container.resolve<LastWatchedLinearStore>('lastWatchedLinearStore');
          await lastWatchedLinear.init();
        })(),
        (async () => {
          await this.loadUserData();
          tvpilJournal.log(createUserStoreEvent('UserDataReady'));

          await Promise.all([this.updateLaunchdarklyUser(), appStore.loadUserChannelStats()]);

          const providersRepository = container.resolve<ProvidersRepository>('providersRepository');
          providersRepository.init();

          tvpilJournal.log(createUserStoreEvent('LdUserIdentified'));
        })(),
        (async () => {
          await this.deviceStore?.getUserHousehold();

          tvpilJournal.log(createUserStoreEvent('HouseholdReady'));
        })(),
      ]);
    } else {
      tvpilJournal.log(createUserStoreEvent('SetupUserWithSilentLogin'));

      await loginStore.loginAnonymousUser(this.appParametersStore.params);

      tvpilJournal.log(createUserStoreEvent('AnonymousLogin'));

      if (loginStore.isAnonymous) {
        await loginStore.loginSilentUser();

        tvpilJournal.log(createUserStoreEvent('SilentLogin'));
      }

      if (loginStore.isAnonymous && loginStore.initialJwtToken && loginStore.initialJwtToken.jwt) {
        try {
          await loginStore.setupServiceList(loginStore.initialJwtToken);
        } catch (e) {
          console.error('initialJwtToken error');
        } finally {
          loginStore.initialJwtToken = undefined;
        }
      }
    }

    tvpilJournal.log(createUserStoreEvent('SetupUserEnd'));
  }

  async getActualFromDynamicData(key: DynamicDataTypeEnum) {
    await this.loadUserData();
    return this.userData?.dynamicData?.[key]?.value;
  }

  getFromDynamicData(key: DynamicDataTypeEnum, userData = this.userData) {
    return userData?.dynamicData?.[key]?.value;
  }

  async loadUserData(newUserData?: KalturaOTTUser): Promise<boolean> {
    let user = newUserData;
    if (!user) {
      user = await lastValueFrom(getUser());
      if (!user) {
        return false;
      }
    }

    this.customerType = this.getFromDynamicData(DynamicDataTypeEnum.customerType, user) || '';
    kuxUpdateConfig({ customerType: this.getFromDynamicData(DynamicDataTypeEnum.customerType, user) || '' });
    await Promise.all([this.getMultiCastQuality(user), this.setupIdleSelection(user.id)]);

    this.userData = user;
    if (featureEnabler.variation('FEATURE_nPVR', false)) {
      await this.recordRepository?.getAvailableSpace();

      tvpilJournal.log(createUserStoreEvent('RecordQuotaReady'));
    }

    this.isReady = true;

    return true;
  }

  private async getMultiCastQuality(userData: KalturaOTTUser) {
    const defaultCastQuality = 'HD';
    const householdCastQuality = this.getFromDynamicData(DynamicDataTypeEnum.householdBandwidth, userData);

    this.multiCastQuality = getMulticastQualityValue(
      householdCastQuality,
      this.userBitrate ?? 0,
      defaultCastQuality,
      this.dmsStore?.dms?.bitrateLimitationManagement?.localVqSettings,
    );
  }

  async updateLaunchdarklyUser(): Promise<boolean> {
    if (!this.userData || !this.dmsStore?.dms?.launchDarkly || !this.appParametersStore.params?.featureEnabledOptions)
      return false;

    const newUser: FFUser = {
      anonymous: false,
      name: this.userData.username,
      key: `${this.appParametersStore.params.udid}`,
    };
    await featureEnabler.updateUser(newUser, {
      consumerEmail: this.userData?.email || this.userData.username,
    });
    setEpgLimitCache(featureEnabler.variation('epgCacheChannelPerDay', -1));

    return true;
  }

  async initBitrate() {
    const bitrateLimit = this.dmsStore?.dms.bitrateLimitationManagement.bitrateLimit;

    const cache = tvpilGetLocalCache();

    if (this.dataSavingMode) {
      this.userBitrate = bitrateLimit;
      return;
    }

    const bitrate = (await cache.getItem(USER_BITRATE)) as number;
    const isBitrateValid =
      bitrate === 0 || this.dmsStore?.videoQualitySettings.some(([, q]) => q['top_range_bitrate'] === bitrate);

    this.userBitrate = isBitrateValid ? bitrate : undefined;
  }

  /**
   *  Set video bitrate per device
   * @param bitrate: number
   *
   * @returns  Promise<void>
   */
  @action
  @errorHandling({ type: ErrorPriority.low })
  @toast('setBitrate')
  async setBitrate(bitrate?: number): Promise<void> {
    this.userBitrate = bitrate;
    const cache = tvpilGetLocalCache();
    await cache.setItem(USER_BITRATE, bitrate, KS_TTL);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    await this.getMultiCastQuality(this.userData!);
  }

  @action
  @errorHandling({ type: ErrorPriority.low })
  @toast('setDataSavingMode')
  async setDataSavingMode(mode: boolean): Promise<void> {
    this.dataSavingMode = mode;
    await this.initBitrate();

    const cache = tvpilGetLocalCache();
    await cache.setItem(DATA_SAVING_MODE, mode, KS_TTL);
  }

  async initDataSavingMode() {
    const cache = tvpilGetLocalCache();
    this.dataSavingMode = (await cache.getItem(DATA_SAVING_MODE)) as boolean;

    await this.initBitrate();
  }

  public toggleIdleSelection() {
    this.idleSelection = !this.idleSelection;
    this.persistIdleSelection(this.idleSelection);
  }

  private async setupIdleSelection(userId: string) {
    this.idleSelection = (await getCacheValue<boolean>(`${IDLE_USER_SELECTION_KEY}_${userId}`)) || false;
  }

  private async persistIdleSelection(value: boolean) {
    // @ts-expect-error TODO Fix
    setCacheValue(`${IDLE_USER_SELECTION_KEY}_${this.userData.id}`, value);
  }

  @action.bound
  resetStore() {
    this.userData = undefined;
  }
}
